/**
 * watchlistApi.ts -- MER Algo Footprints v9.2.0
 *
 * Phase 4A: thin client wrapper around the /api/watchlist endpoints
 * (server/watchlistRoutes.js). Provides cross-device persistence for
 * the user's watchlist, replacing the localStorage-only approach.
 *
 * Three functions:
 *   - fetchWatchlist()           -- GET    /api/watchlist
 *   - addWatchlistSymbol(symbol) -- POST   /api/watchlist
 *   - removeWatchlistSymbol(s)   -- DELETE /api/watchlist/:symbol
 *
 * NOTES on resilience:
 *
 *   fetchWatchlist returns null on ANY failure (network error, 401, 5xx)
 *   so app load never blocks on a watchlist fetch. The caller can
 *   distinguish 'no server data available' (null) from 'server has empty
 *   watchlist' (empty array) -- this distinction matters for the migration
 *   logic in useUserPrefsHydration.
 *
 *   addWatchlistSymbol special-cases the 409 (duplicate) response as a
 *   no-op success. The desired state (symbol on watchlist) is already
 *   true, so there's nothing to reconcile.
 *
 *   removeWatchlistSymbol leans on server idempotency -- the server
 *   returns 200 whether or not the row existed.
 *
 * MATCHES the style of userPrefsApi.ts (commit 2.6/2.7 Phase 3 pattern).
 */

const ENDPOINT = '/api/watchlist';

/**
 * Server response shape from GET /api/watchlist.
 * (Only the fields we need; the server returns more for the analysis UI.)
 */
interface WatchlistResponse {
  items: Array<{
    symbol:   string;
    added_at: string;
  }>;
}

/**
 * Fetch the user's stored watchlist as an array of symbol strings.
 * Returns null on any failure -- never throws. Safe to call during app
 * initialization. Returns [] when the user has no symbols server-side
 * (distinct from null which means we couldn't reach the server).
 */
export async function fetchWatchlist(): Promise<string[] | null> {
  try {
    const r = await fetch(ENDPOINT, { credentials: 'include' });
    if (!r.ok) return null;
    const data = (await r.json()) as WatchlistResponse;
    if (!data || !Array.isArray(data.items)) return null;
    return data.items.map(item => item.symbol);
  } catch {
    return null;
  }
}

/**
 * Add a symbol to the user's server-side watchlist.
 * Throws on failure (other than duplicate-symbol 409, which is treated
 * as a no-op success since the desired state is already true).
 */
export async function addWatchlistSymbol(symbol: string): Promise<void> {
  let r: Response;
  try {
    r = await fetch(ENDPOINT, {
      method:      'POST',
      credentials: 'include',
      headers:     { 'Content-Type': 'application/json' },
      body:        JSON.stringify({ symbol }),
    });
  } catch {
    throw new Error('NETWORK_ERROR');
  }

  // 409 = already exists. Desired state achieved; treat as success.
  if (r.status === 409) return;

  if (!r.ok) {
    let reason = `HTTP_${r.status}`;
    try {
      const data = await r.json();
      if (data && typeof data.error === 'string') reason = data.error;
    } catch { /* response wasn't JSON; keep HTTP_xxx */ }
    throw new Error(reason);
  }
}

/**
 * Remove a symbol from the user's server-side watchlist.
 * Throws on failure. Server is idempotent -- returns 200 whether or not
 * the row existed -- so callers don't need to check membership first.
 */
export async function removeWatchlistSymbol(symbol: string): Promise<void> {
  let r: Response;
  try {
    r = await fetch(`${ENDPOINT}/${encodeURIComponent(symbol)}`, {
      method:      'DELETE',
      credentials: 'include',
    });
  } catch {
    throw new Error('NETWORK_ERROR');
  }

  if (!r.ok) {
    let reason = `HTTP_${r.status}`;
    try {
      const data = await r.json();
      if (data && typeof data.error === 'string') reason = data.error;
    } catch { /* response wasn't JSON; keep HTTP_xxx */ }
    throw new Error(reason);
  }
}
